import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import ReactPaginate from "react-paginate";
import * as Select from "@radix-ui/react-select";
import SearchInput from "@components/ui/searchInput";
import Table from "@components/Table";
import LoadingScreen from "@components/LoadingScreen";
import { getUsers, IUser } from "@redux/slices/auth";
import useAuth from "@hooks/useAuth";
import {
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";

const Users = () => {
  const [itemOffset, setItemOffset] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [reload, setReload] = useState<boolean>(false);
  const [users, setUsers] = useState<IUser[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>(""); // State for search term
  const navigate = useNavigate();
  let filteredData: IUser[] | undefined;

  useEffect(() => {
    setLoading(true);
    getData();
  }, [reload]);

  const getData = async () => {
    try {
      const data = await getUsers();

      if (data) {
        const validData = data.filter((item: IUser) => item.created_at);

        const sortedData = validData.sort((a, b) => {
          const dateA = new Date(a.created_at as string).getTime();
          const dateB = new Date(b.created_at as string).getTime();
          return dateB - dateA;
        });

        setUsers(sortedData);
        console.log(sortedData);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search term
  const searchResults = users.filter(
    (user) =>
      `${user.first_name} ${user.last_name}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const endOffset = itemOffset + itemsPerPage;
  filteredData = searchResults.slice(itemOffset, endOffset); // Use searchResults for pagination
  const pageCount = Math.ceil(searchResults.length / itemsPerPage);

  const handlePageClick = (event: { selected: number }) => {
    const newOffset = (event.selected * itemsPerPage) % searchResults.length;
    setItemOffset(newOffset);
  };

  const handleItemsPerPageChange = (value: string) => {
    const perPage = parseInt(value, 10);
    setItemsPerPage(perPage);
    setItemOffset(0); // Reset offset to first page
  };

  return (
    <div>
      {loading ? (
        <LoadingScreen />
      ) : (
        <div>
          <div className="md:flex justify-end items-center mt-7">
            <div className="sm:flex items-center">
              <div className="me-2">
                <div className="relative">
                  <input
                    className="appearance-none placeholder:text-[#B5B7C0] placeholder:text-sm placeholder:ps-1 border-0 pl-10 border-[#e2e8f0] bg-white shadow-sm transition-colors rounded-md w-full py-2 px-3 text-gray-800 leading-tight focus:outline-none"
                    id="username"
                    type="text"
                    placeholder="Search"
                    value={searchTerm} // Bind input to searchTerm
                    onChange={(e) => setSearchTerm(e.target.value)} // Update searchTerm on input change
                  />

                  <div className="absolute left-0 inset-y-0 flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 ml-3 text-gray-400 hover:text-gray-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="#71717A"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Table
            textIndex={2}
            tableHead={[{ name: "No" }, { name: "Name" }, { name: "Email" }]}
          >
            {filteredData?.map((val, index) => (
              <tr
                key={val.id}
                className="text-sm text-[#71717A] text-left border-b last:border-0 border-[#EAECF0] cursor-pointer"
                onClick={() =>
                  navigate(
                    `/campaignMessages/publishedMessages/${Number(
                      window.location.pathname.split("/")[3]
                    )}/${val.id}`
                  )
                }
              >
                <td className="p-5 w-16">{itemOffset + index + 1}</td>
                <td className="p-5">{val.first_name + " " + val.last_name}</td>
                <td className="p-5">{val.email}</td>
              </tr>
            ))}
          </Table>
          <div className="flex flex-col md:flex-row justify-between items-center mt-8 pb-32">
            <div className="me-2 w-32 md:mb-0 mb-4">
              <Select.Root onValueChange={handleItemsPerPageChange}>
                <SelectTrigger className="SelectTrigger bg-white border-none">
                  <SelectValue placeholder={`${itemsPerPage} per Page`} />
                </SelectTrigger>
                <SelectContent className="SelectContent">
                  <SelectItem value="10">10 per page</SelectItem>
                  <SelectItem value="15">15 per page</SelectItem>
                  <SelectItem value="20">20 per page</SelectItem>
                </SelectContent>
              </Select.Root>
            </div>
            <div className="pagination">
              <ReactPaginate
                breakLabel="..."
                nextLabel={window.innerWidth > 576 ? "Next  →" : ">"}
                onPageChange={handlePageClick}
                pageRangeDisplayed={5}
                pageCount={pageCount}
                previousLabel={window.innerWidth > 576 ? "←  Prev" : "<"}
                renderOnZeroPageCount={null}
                activeClassName="bg-white"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
